import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Store as ServiceIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Business as EntityIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import api from '../../../services/api';
import { useTranslation } from 'react-i18next';

interface SispService {
  id: string;
  desig: string;
  codigo: string;
  descr: string;
  sispService: string;
  entidadeId: string;
  entidadeNome: string;
  entidadeSisp: string;
  servicoId: string;
  servicoNome: string;
  tipoServicoId: string;
  tipoServico: string;
  categoriaServicoId: string;
  categoriaServico: string;
  estado: string;
  dataRegisto: string;
}

interface SelectOption {
  id: string;
  nome: string;
  codigo?: string;
}

interface EntityOption {
  id: string;
  desig: string;
  codigo: string;
  entidadeSisp: string;
}

const SispServices: React.FC = () => {
  const { t } = useTranslation();
  const [services, setServices] = useState<SispService[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, activos: 0, inactivos: 0, entidades: 0 });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Lookup data
  const [entities, setEntities] = useState<EntityOption[]>([]);
  const [serviceTypes, setServiceTypes] = useState<SelectOption[]>([]);
  const [serviceCategories, setServiceCategories] = useState<SelectOption[]>([]);

  // Dialog state
  const [dialog, setDialog] = useState<'create' | 'edit' | 'view' | null>(null);
  const [selected, setSelected] = useState<SispService | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    desig: '',
    descr: '',
    sispService: '',
    entidadeId: '',
    servicoId: '',
    tipoServicoId: '',
    categoriaServicoId: '',
    estado: 'A',
  });

  // Toast
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const showToast = (message: string, severity: 'success' | 'error') => setToast({ open: true, message, severity });

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/parametrizacoes/sisp-services');
      if (response.data.success) {
        setServices(response.data.data || []);
        if (response.data.stats) setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching SISP services:', error);
      showToast(t('sispServices.errorLoading'), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLookups = useCallback(async () => {
    try {
      const [entRes, typesRes, catsRes] = await Promise.all([
        api.get('/admin/entities?limit=500&status=A'),
        api.get('/admin/services/types'),
        api.get('/admin/services/categories'),
      ]);
      if (entRes.data.success) {
        setEntities((entRes.data.data || []).map((e: any) => ({
          id: e.id, desig: e.desig, codigo: e.codigo, entidadeSisp: e.entidadeSisp || '',
        })));
      }
      if (typesRes.data.success) setServiceTypes(typesRes.data.data || []);
      if (catsRes.data.success) setServiceCategories(catsRes.data.data || []);
    } catch (error) {
      console.error('Error fetching lookups:', error);
    }
  }, []);

  useEffect(() => {
    fetchServices();
    fetchLookups();
  }, [fetchServices, fetchLookups]);

  // Filtering
  const filtered = services.filter(s => {
    const matchesSearch = searchTerm === '' ||
      s.desig?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.entidadeNome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.sispService?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && (s.estado === 'A' || s.estado === 'S')) ||
      (statusFilter === 'inactive' && (s.estado === 'I' || s.estado === 'N'));
    const matchesEntity = entityFilter === 'all' || s.entidadeId === entityFilter;
    const matchesType = typeFilter === 'all' || s.tipoServicoId === typeFilter;
    return matchesSearch && matchesStatus && matchesEntity && matchesType;
  });

  // Unique entities in data for filter dropdown
  const entityOptions = Array.from(new Set(services.map(s => s.entidadeId).filter(Boolean)))
    .map(id => ({ id, nome: services.find(s => s.entidadeId === id)?.entidadeNome || id }));

  // Handlers
  const handleOpenCreate = () => {
    setSelected(null);
    setFormData({ desig: '', descr: '', sispService: '', entidadeId: '', servicoId: '', tipoServicoId: '', categoriaServicoId: '', estado: 'A' });
    setDialog('create');
  };

  const handleOpenEdit = (service: SispService) => {
    setSelected(service);
    setFormData({
      desig: service.desig || '',
      descr: service.descr || '',
      sispService: service.sispService || '',
      entidadeId: service.entidadeId || '',
      servicoId: service.servicoId || '',
      tipoServicoId: service.tipoServicoId || '',
      categoriaServicoId: service.categoriaServicoId || '',
      estado: service.estado === 'S' ? 'A' : service.estado || 'A',
    });
    setDialog('edit');
  };

  const handleOpenView = (service: SispService) => {
    setSelected(service);
    setDialog('view');
  };

  const handleClose = () => {
    setDialog(null);
    setSelected(null);
  };

  const handleSave = async () => {
    if (!formData.desig.trim()) {
      showToast(t('sispServices.designationRequired'), 'error');
      return;
    }
    setSaving(true);
    try {
      if (dialog === 'create') {
        await api.post('/admin/parametrizacoes/sisp-services', formData);
        showToast(t('sispServices.serviceCreated'), 'success');
      } else if (dialog === 'edit' && selected) {
        await api.put(`/admin/parametrizacoes/sisp-services/${selected.id}`, formData);
        showToast(t('sispServices.serviceUpdated'), 'success');
      }
      handleClose();
      fetchServices();
    } catch (error: any) {
      showToast(error?.response?.data?.error || t('sispServices.errorSaving'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (service: SispService) => {
    if (!window.confirm(`${t('sispServices.confirmDelete')} "${service.desig || service.sispService}"?`)) return;
    try {
      await api.delete(`/admin/parametrizacoes/sisp-services/${service.id}`);
      showToast(t('sispServices.serviceDeleted'), 'success');
      fetchServices();
    } catch (error: any) {
      showToast(error?.response?.data?.error || t('sispServices.errorDeleting'), 'error');
    }
  };

  const getStatusChip = (estado: string) => {
    const isActive = estado === 'A' || estado === 'S';
    return (
      <Chip
        size="small"
        color={isActive ? 'success' : 'default'}
        label={isActive ? t('common.active') : t('common.inactive')}
        icon={isActive ? <ActiveIcon /> : <InactiveIcon />}
      />
    );
  };

  const formatDate = (date: string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' });
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {t('sispServices.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('sispServices.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            {t('sispServices.newService')}
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchServices}>
            {t('common.refresh')}
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ServiceIcon color="primary" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('sispServices.totalServices')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ActiveIcon color="success" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.activos}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('sispServices.active')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InactiveIcon color="disabled" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.inactivos}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('sispServices.inactive')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EntityIcon color="info" />
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.entidades}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('sispServices.entities')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('sispServices.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2.5 }}>
              <TextField fullWidth size="small" select label={t('common.entity')} value={entityFilter}
                onChange={(e) => { setEntityFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">{t('sispServices.allEntities')}</MenuItem>
                {entityOptions.map(e => (
                  <MenuItem key={e.id} value={e.id}>{e.nome}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 2.5 }}>
              <TextField fullWidth size="small" select label={t('sispServices.serviceType')} value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">{t('sispServices.allTypes')}</MenuItem>
                {serviceTypes.map(st => (
                  <MenuItem key={st.id} value={st.id}>{st.nome}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField fullWidth size="small" select label={t('common.status')} value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">{t('sispServices.allTypes')}</MenuItem>
                <MenuItem value="active">{t('sispServices.active')}</MenuItem>
                <MenuItem value="inactive">{t('sispServices.inactive')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.code')}</TableCell>
                    <TableCell>{t('sispServices.designation')}</TableCell>
                    <TableCell>{t('common.entity')}</TableCell>
                    <TableCell>{t('common.type')}</TableCell>
                    <TableCell>{t('common.category')}</TableCell>
                    <TableCell width={110}>{t('common.dateRegister')}</TableCell>
                    <TableCell width={100}>{t('common.status')}</TableCell>
                    <TableCell width={120} align="center">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">{t('sispServices.noServices')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((service) => (
                        <TableRow key={service.id} hover>
                          <TableCell>
                            <Chip label={service.codigo || '-'} size="small" variant="outlined"
                              sx={{ fontFamily: 'monospace', fontWeight: 600 }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{service.desig || service.sispService || '-'}</Typography>
                            {service.sispService && service.desig && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                SISP: {service.sispService}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{service.entidadeNome || '-'}</Typography>
                            {service.entidadeSisp && (
                              <Chip label={service.entidadeSisp} size="small" variant="outlined"
                                sx={{ fontFamily: 'monospace', fontSize: 10, height: 18, mt: 0.5 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{service.tipoServico || '-'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{service.categoriaServico || '-'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatDate(service.dataRegisto)}</Typography>
                          </TableCell>
                          <TableCell>{getStatusChip(service.estado)}</TableCell>
                          <TableCell align="center">
                            <IconButton size="small" onClick={() => handleOpenView(service)} title={t('common.view')}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleOpenEdit(service)} title={t('common.edit')}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(service)} title={t('common.delete')}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage={t('common.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialog === 'create' || dialog === 'edit'} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ServiceIcon />
          {dialog === 'create' ? t('sispServices.newSispService') : t('sispServices.editSispService')}
          {selected?.codigo && (
            <Chip label={selected.codigo} size="small" variant="outlined" sx={{ fontFamily: 'monospace', ml: 1 }} />
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label={`${t('sispServices.designation')} *`}
                value={formData.desig}
                onChange={(e) => setFormData({ ...formData, desig: e.target.value })}
                helperText={t('sispServices.serviceNameHelper')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label={t('common.description')}
                value={formData.descr}
                onChange={(e) => setFormData({ ...formData, descr: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label={t('sispServices.sispCode')}
                value={formData.sispService}
                onChange={(e) => setFormData({ ...formData, sispService: e.target.value })}
                helperText={t('sispServices.sispCodeHelper')}
                slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('common.status')}
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
              >
                <MenuItem value="A">{t('common.active')}</MenuItem>
                <MenuItem value="I">{t('common.inactive')}</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('common.entity')}
                value={formData.entidadeId}
                onChange={(e) => setFormData({ ...formData, entidadeId: e.target.value })}
                helperText={t('sispServices.entityHelper')}
              >
                <MenuItem value="">{t('sispServices.none')}</MenuItem>
                {entities.map(e => (
                  <MenuItem key={e.id} value={e.id}>
                    {e.desig} {e.entidadeSisp ? `(SISP: ${e.entidadeSisp})` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('sispServices.serviceTypeLabel')}
                value={formData.tipoServicoId}
                onChange={(e) => setFormData({ ...formData, tipoServicoId: e.target.value })}
              >
                <MenuItem value="">{t('sispServices.noneType')}</MenuItem>
                {serviceTypes.map(st => (
                  <MenuItem key={st.id} value={st.id}>{st.nome}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('common.category')}
                value={formData.categoriaServicoId}
                onChange={(e) => setFormData({ ...formData, categoriaServicoId: e.target.value })}
              >
                <MenuItem value="">{t('sispServices.none')}</MenuItem>
                {serviceCategories.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.desig.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {saving ? t('common.saving') : dialog === 'create' ? t('common.create') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={dialog === 'view'} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ServiceIcon />
            <Typography variant="h6">{selected?.desig || selected?.sispService}</Typography>
            {selected?.codigo && (
              <Chip label={selected.codigo} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
            )}
          </Box>
          <IconButton onClick={handleClose}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">{t('common.code')}</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{selected.codigo || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">{t('sispServices.sispCode')}</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{selected.sispService || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="caption" color="text.secondary">{t('sispServices.designation')}</Typography>
                <Typography variant="body1">{selected.desig || '-'}</Typography>
              </Grid>
              {selected.descr && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="text.secondary">{t('common.description')}</Typography>
                  <Typography variant="body2">{selected.descr}</Typography>
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">{t('common.entity')}</Typography>
                <Typography variant="body1">{selected.entidadeNome || '-'}</Typography>
                {selected.entidadeSisp && (
                  <Chip label={`SISP: ${selected.entidadeSisp}`} size="small" variant="outlined"
                    sx={{ fontFamily: 'monospace', mt: 0.5, fontSize: 11 }} />
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">{t('common.status')}</Typography>
                <Box sx={{ mt: 0.5 }}>{getStatusChip(selected.estado)}</Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">{t('sispServices.serviceTypeLabel')}</Typography>
                <Typography variant="body1">{selected.tipoServico || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">{t('common.category')}</Typography>
                <Typography variant="body1">{selected.categoriaServico || '-'}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" color="text.secondary">{t('common.dateRegister')}</Typography>
                <Typography variant="body1">{formatDate(selected.dataRegisto)}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common.close')}</Button>
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => { if (selected) handleOpenEdit(selected); }}>
            {t('common.edit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SispServices;
